/**
 * Live presence marks (utils/presenceClient.js, utils/presenceModel.js), as the collaboration design draws them:
 * people round with their initials, AI agents square with a dashed edge and the initials of the person driving them;
 * every mark says who in words too (its label), never a colour alone.
 *
 * - ParticipantMark: one avatar.
 * - PresenceStack: the project header's "who is here" (at most 4, then +N), with a list on hover/focus.
 * - TreePresenceMarker: a file tree row's "LB editing" / "Flow · LB writing" / a folder's stack.
 * - EditingNowStrip: a kanban card's live strip.
 * - ModifiedMarker: a tree row's "M · LB", changed by others since you last left the project (phase 4).
 * - PresenceEditorBar: the editor's soft warnings and a viewer's read only.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { message, Popover } from 'antd';
import { UsersThree } from '@phosphor-icons/react';
import { ResponsivePopover } from '../collab/BottomSheet';
import { inkFor, onInkFor } from '../../utils/participantColors';
import {
  activityOf, agentLinesIn, agentName, agentStepLabel, canPause, editingNow, firstName, followersOf, lineInRanges, linesLabel,
  modelLabel, modifiedBy, modifiedTitle, othersOf, pausedLabel, softWarningFor,
} from '../../utils/presenceModel';
import { clearPresenceError, followPerson, pauseAgent } from '../../utils/presenceClient';
import { previewOfAgent, previewOfPerson, seeLabel } from '../../utils/collab3Model';
import AgentActions from '../collab/AgentActions';
import SharedPreviewThumbnail from '../collab/SharedPreviewThumbnail';
import { openMessages } from '../collab/Messages';
import { canMessageAgents, canSendMessages } from '../../utils/messagesModel';
import VendorName from '../sandbox/VendorName';
import './Presence.css';
import '../collab/Collab.css';

export function ParticipantMark({ initials, slot, agent = false, size = 24, label, idle = false, paused = false }) {
  return (
    <span
      aria-label={label}
      className={`presence-mark${agent ? ' is-agent' : ''}${idle ? ' is-idle' : ''}${paused ? ' is-paused' : ''}`}
      role="img"
      style={{ '--presence-ink': inkFor(slot), '--presence-on-ink': onInkFor(), width: size, height: size, fontSize: Math.round(size * 0.4) }}
      title={label}
    >
      {initials || '?'}
    </span>
  );
}

ParticipantMark.propTypes = {
  agent: PropTypes.bool,
  idle: PropTypes.bool,
  initials: PropTypes.string,
  label: PropTypes.string,
  paused: PropTypes.bool,
  size: PropTypes.number,
  slot: PropTypes.number,
};

const personLabel = (person) => `${person.name}${person.role === 'viewer' ? ' (viewer)' : ''} · ${activityOf(person)}${person.state === 'idle' ? ' · idle' : ''}`;
const agentLabel = (agent, you) => (agent.paused
  ? `${agentName(agent, you)} · ${pausedLabel(agent, you)}`
  : `${agentName(agent, you)} · writing ${(agent.paths || []).map((p) => p.split('/').pop()).join(', ') || 'files'}`);

/** Agents worth showing: writing now, or paused (a paused agent stays listed until resumed). */
export const shownAgents = (snapshot) => (snapshot?.agents || []).filter((agent) => (agent.paths || []).length > 0 || agent.paused);

/** The presence list also shows an agent that only built or checked a preview (to see what it saw). */
export const listedAgents = (snapshot) => (snapshot?.agents || []).filter((agent) => (agent.paths || []).length > 0 || agent.paused || agent.preview);

/**
 * "See what X sees": opens YOUR preview pane on their service at their route (Canvas listens for `collab:openPreview`;
 * the same preview URL, so the preview's own access rules apply). Nothing is shared but the route.
 */
export function openPreviewFor(projectId, target) {
  if (!target || typeof window === 'undefined') { return; }
  window.dispatchEvent(new CustomEvent('collab:openPreview', { detail: { projectId, service: target.service || null, route: target.route || '/' } }));
}

/**
 * The list behind the header's avatars: follow a person (your editor opens their file and scrolls with them), pause or
 * resume an AI agent (owner: any, editor: the ones they drive).
 */
export function PresenceMenu({ snapshot, projectId }) {
  const you = snapshot?.you;
  const others = othersOf(snapshot);
  const agents = listedAgents(snapshot);
  const followers = followersOf(snapshot);
  // Message a person (anyone who can open the project, a viewer too) or an agent here (not a viewer: vfs-service
  // refuses their note for the agents); collab/Messages.jsx.
  const canMessage = Boolean(projectId) && canSendMessages(you?.role);
  const canMessageAgent = Boolean(projectId) && canMessageAgents(you?.role);
  return (
    <div className="collab-presence-menu" data-testid="presence-menu">
      {others.map((person) => {
        const following = snapshot?.following === person.id;
        return (
          <div className="collab-presence-row" key={person.id}>
            <ParticipantMark idle={person.state === 'idle'} initials={person.initials} size={22} slot={person.slot} />
            <span>{person.name}<span className="collab-muted collab-small"> · {activityOf(person)}</span></span>
            {previewOfPerson(person) && (
              <button className="collab-link" data-testid="see-preview" onClick={() => openPreviewFor(projectId, previewOfPerson(person))} title={`Opens your preview at ${previewOfPerson(person).route}`} type="button">
                {seeLabel(person, you)}
              </button>
            )}
            {person.followable !== false && (
              <button className="collab-link" onClick={() => followPerson(projectId, following ? null : person.id)} type="button">
                {following ? 'Stop following' : 'Follow'}
              </button>
            )}
            {canMessage && person.sub && (
              <button aria-label={`Message ${person.name || 'them'}`} className="collab-link" data-testid="presence-message-person" onClick={() => openMessages({ projectId, view: 'compose', recipient: `sub:${person.sub}`, place: null })} type="button">
                Message
              </button>
            )}
          </div>
        );
      })}
      {agents.map((agent) => (
        <div className="collab-presence-row" key={agent.id}>
          <ParticipantMark agent initials={agent.driver?.initials || 'AI'} paused={Boolean(agent.paused)} size={22} slot={agent.slot} />
          <span>
            <VendorName name={agent.label}>{agentName(agent, you)}</VendorName>
            {agent.paused && <span className="collab-paused"> · {pausedLabel(agent, you)}</span>}
            {!agent.paused && (agent.paths || []).length > 0 && <span className="collab-muted collab-small"> · writing {(agent.paths || []).map((p) => p.split('/').pop()).join(', ')}{[agentStepLabel(agent), modelLabel(agent.model)].filter(Boolean).map((part) => ` · ${part}`).join('')}</span>}
            {!agent.paused && !(agent.paths || []).length && agent.preview && <span className="collab-muted collab-small"> · checked the preview</span>}
          </span>
          {previewOfAgent(agent) && (
            <button className="collab-link" data-testid="see-preview" onClick={() => openPreviewFor(projectId, previewOfAgent(agent))} type="button">
              {seeLabel(agent, you)}
            </button>
          )}
          {canPause(agent, snapshot) && (
            <button className="collab-link" onClick={() => pauseAgent(projectId, agent.id, !agent.paused)} type="button">
              {agent.paused ? 'Resume' : 'Pause'}
            </button>
          )}
          {canMessageAgent && (
            <button aria-label={`Message ${agentName(agent, you)}`} className="collab-link" data-testid="presence-message-agent" onClick={() => openMessages({ projectId, view: 'compose', recipient: `agent:${agent.id}`, agent, place: null })} type="button">
              Message
            </button>
          )}
        </div>
      ))}
      {followers.length > 0 && (
        <div className="collab-muted collab-small" data-testid="followers">
          {followers.map((p) => firstName(p.name)).join(', ')} {followers.length === 1 ? 'is' : 'are'} following you
        </div>
      )}
      {projectId && <SharedPreviewThumbnail projectId={projectId} snapshot={snapshot} />}
    </div>
  );
}

PresenceMenu.propTypes = {
  projectId: PropTypes.string,
  snapshot: PropTypes.object,
};

/**
 * The project header's avatars: the others first, then the agents; nothing when you are alone. `compact` (phones and
 * tablets): a count chip instead of the avatars; it opens the same list, as a bottom sheet on a phone.
 */
export function PresenceStack({ snapshot, max = 4, projectId = null, compact = false, onOpen = null }) {
  const others = othersOf(snapshot);
  const agents = shownAgents(snapshot);
  const error = snapshot?.error;
  useEffect(() => {
    if (error && projectId) {
      message.error(error.message);
      clearPresenceError(projectId);
    }
  }, [error, projectId]);
  const followers = followersOf(snapshot);
  const marks = [
    ...others.map((person) => ({ key: person.id, person })),
    ...agents.map((agent) => ({ key: agent.id, agent })),
  ];
  if (marks.length === 0) { return null; }
  const shown = marks.slice(0, max);
  const more = marks.length - shown.length;
  const summary = marks.map((mark) => (mark.person ? personLabel(mark.person) : agentLabel(mark.agent, snapshot.you))).join('\n');
  if (compact) {
    const label = `${marks.length} here now${followers.length > 0 ? `, ${followers.length} following you` : ''}${agents.length > 0 ? `, ${agents.length} AI agent${agents.length === 1 ? '' : 's'}` : ''}`;
    const chip = (
      <button aria-label={`${label}: who and what they do`} className="presence-chip" data-testid="presence-chip" title={projectId ? undefined : summary} type="button">
        <UsersThree aria-hidden size={18} />
        <span>{marks.length}</span>
        {agents.some((a) => a.paused) && <span className="presence-chip-paused" title="An agent is paused">paused</span>}
      </button>
    );
    if (!projectId) { return chip; }
    return (
      <ResponsivePopover content={<PresenceMenu projectId={projectId} snapshot={snapshot} />} popoverTitle="Here now" testId="presence-sheet" title={`Here now · ${marks.length}`}>
        {chip}
      </ResponsivePopover>
    );
  }
  const stack = (
    <div aria-label={`Here now: ${marks.length}`} className="presence-stack" data-testid="presence-stack" role="group" style={projectId ? { cursor: 'pointer' } : undefined} tabIndex={0} title={projectId ? undefined : summary}>
      <span className="presence-stack-count">{followers.length > 0 ? `${marks.length} here · ${followers.length} following you` : `${marks.length} here`}</span>
      {shown.map((mark) => (mark.person ? (
        <ParticipantMark idle={mark.person.state === 'idle'} initials={mark.person.initials} key={mark.key} label={personLabel(mark.person)} slot={mark.person.slot} />
      ) : (
        <ParticipantMark agent initials={mark.agent.driver?.initials || 'AI'} key={mark.key} label={agentLabel(mark.agent, snapshot.you)} paused={Boolean(mark.agent.paused)} slot={mark.agent.slot} />
      )))}
      {more > 0 && <span className="presence-more">+{more}</span>}
    </div>
  );
  if (!projectId) { return stack; }
  // Desktop with the docked "In this Project" panel: the avatars open it instead of a popover.
  if (onOpen) {
    return React.cloneElement(stack, {
      'aria-label': `Here now: ${marks.length}. Show who and what they do`,
      onClick: onOpen,
      onKeyDown: (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(); } },
      role: 'button',
    });
  }
  return (
    <Popover content={<PresenceMenu projectId={projectId} snapshot={snapshot} />} placement="bottomRight" title="Here now" trigger="click">
      {stack}
    </Popover>
  );
}

PresenceStack.propTypes = {
  compact: PropTypes.bool,
  max: PropTypes.number,
  onOpen: PropTypes.func,
  projectId: PropTypes.string,
  snapshot: PropTypes.object,
};

/** A tree row's marker: who, then what ("editing", "viewing", an agent "writing"); a folder rolls up into a stack. */
export function TreePresenceMarker({ presence, folder = false, you }) {
  const { people = [], agents = [] } = presence || {};
  if (people.length === 0 && agents.length === 0) { return null; }
  if (folder) {
    const marks = [...people.map(({ person }) => person), ...agents];
    return (
      <span className="tree-presence" data-testid="tree-presence" title={marks.map((m) => (m.driver ? agentName(m, you) : m.name)).join(', ')}>
        {marks.slice(0, 3).map((mark) => (
          <ParticipantMark agent={Boolean(mark.driver || mark.label)} initials={mark.driver?.initials || mark.initials || 'AI'} key={mark.id} size={16} slot={mark.slot} />
        ))}
      </span>
    );
  }
  const [agent] = agents;
  if (agent) {
    return (
      <span className="tree-presence" data-testid="tree-presence" title={`${agentName(agent, you)} is writing this file`}>
        <ParticipantMark agent initials={agent.driver?.initials || 'AI'} size={16} slot={agent.slot} />
        <span className="tree-presence-verb">writing</span>
      </span>
    );
  }
  const { person, verb } = people.find((entry) => entry.verb === 'editing') || people[0];
  return (
    <span className="tree-presence" data-testid="tree-presence" title={people.map((p) => `${p.person.name} ${p.verb}`).join(', ')}>
      <ParticipantMark initials={person.initials} size={16} slot={person.slot} />
      <span className="tree-presence-verb">{verb}</span>
      {people.length > 1 && <span className="presence-more">+{people.length - 1}</span>}
    </span>
  );
}

TreePresenceMarker.propTypes = {
  folder: PropTypes.bool,
  presence: PropTypes.object,
  you: PropTypes.object,
};

/** A tree row's "M · LB": others changed this file since you last left the project (hooks/useSinceLastSeen.js). */
export function ModifiedMarker({ entry, you = null }) {
  if (!entry || !(entry.by || []).length) { return null; }
  const title = modifiedTitle(entry, you);
  return (
    <span aria-label={title} className="tree-modified" data-testid="tree-modified" role="img" title={title}>
      M · {modifiedBy(entry)}
    </span>
  );
}

ModifiedMarker.propTypes = {
  entry: PropTypes.object,
  you: PropTypes.object,
};

/** A kanban card's "Editing now" strip: at most two entries, then "+N more". */
export function EditingNowStrip({ snapshot }) {
  const { shown, more } = editingNow(snapshot);
  if (shown.length === 0) { return null; }
  return (
    <div className="editing-now" data-testid="editing-now">
      {shown.map((entry) => (entry.kind === 'agent' ? (
        <div className="editing-now-row is-agent" key={entry.agent.id} style={{ '--presence-ink': inkFor(entry.agent.slot) }}>
          <ParticipantMark agent initials={entry.agent.driver?.initials || 'AI'} size={16} slot={entry.agent.slot} />
          <span><strong>Editing now</strong> · {entry.text}</span>
        </div>
      ) : (
        <div className="editing-now-row" key={entry.person.id} style={{ '--presence-ink': inkFor(entry.person.slot) }}>
          <ParticipantMark initials={entry.person.initials} size={16} slot={entry.person.slot} />
          <span>{entry.text}</span>
        </div>
      )))}
      {more > 0 && <div className="editing-now-more">+{more} more</div>}
    </div>
  );
}

EditingNowStrip.propTypes = {
  snapshot: PropTypes.object,
};

/**
 * Opens the chat an agent runs in (`sessionId`, Flow's conversation id) in this project's workspace, as History does
 * (`?conversationId=`): a client-side navigation (react-router follows `popstate`).
 */
export function openAgentSession(sessionId) {
  if (!sessionId || typeof window === 'undefined') { return; }
  const url = `${window.location.pathname}?conversationId=${encodeURIComponent(sessionId)}`;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** "Open Luca's session" / "Open your session". */
export const sessionLinkLabel = (agent, you) => (agent?.driver && you && agent.driver.id === you.id ? 'Open your session' : `Open ${firstName(agent?.driver?.name || 'its')}’s session`);

/**
 * The bar above the editor, never a lock: a viewer's read only (or an edit the server refused), an AI agent writing
 * this file (stronger while you type: its next write may replace what you type), someone else typing a few lines from
 * you, or live editing not reachable (saves still work).
 */
export function PresenceEditorBar({ snapshot, path, cursor, viewer = false, status = 'off', projectId = null, onSuggest = null, onOpenSession = openAgentSession, actions = null }) {
  const refused = snapshot?.refused && snapshot.refused.path === path;
  if (viewer || refused) {
    return (
      <div className="presence-editor-bar is-viewer" data-testid="presence-editor-bar" role="status">
        <span><strong>You are a viewer</strong> of this project: you can read this file, not change it.{onSuggest ? ' You can suggest a change for an editor to review.' : ' Ask its owner for edit access.'}</span>
        {onSuggest && (
          <button className="collab-link" data-testid="suggest-change" onClick={onSuggest} style={{ marginLeft: 'auto' }} type="button">Suggest a change</button>
        )}
      </div>
    );
  }
  const warning = softWarningFor(snapshot, path, cursor?.line);
  if (warning?.kind === 'agent') {
    const { agent } = warning;
    const ranges = agentLinesIn(agent, path);
    return (
      <div className="presence-editor-bar is-agent" data-testid="presence-editor-bar" role="status" style={{ '--presence-ink': inkFor(agent.slot) }}>
        <ParticipantMark agent initials={agent.driver?.initials || 'AI'} size={18} slot={agent.slot} />
        {agent.paused ? (
          <span><strong>{agentName(agent, snapshot?.you)}</strong> is {pausedLabel(agent, snapshot?.you)}: it may not change files until resumed.</span>
        ) : (
          <span data-testid="agent-writing">
            <strong>{agentName(agent, snapshot?.you)}</strong> is writing {ranges.length ? linesLabel(ranges) : 'this file'}
            {[agentStepLabel(agent), modelLabel(agent.model)].filter(Boolean).map((part) => ` · ${part}`).join('')}.
            {cursor?.typing && (!ranges.length || lineInRanges(ranges, cursor?.line, 2)) ? ' Its next write may replace what you type here.' : ''}
          </span>
        )}
        <span className="presence-editor-bar-actions">
          {!agent.paused && agent.sessionId && onOpenSession && (
            <button className="collab-link" data-testid="open-agent-session" onClick={() => onOpenSession(agent.sessionId, agent)} type="button">
              {sessionLinkLabel(agent, snapshot?.you)}
            </button>
          )}
          {actions ? actions(agent, ranges) : (!agent.paused && projectId && <AgentActions agent={agent} line={cursor?.line ?? null} path={path} projectId={projectId} ranges={ranges} />)}
          {canPause(agent, snapshot) && projectId && (
            <button className="collab-link" onClick={() => pauseAgent(projectId, agent.id, !agent.paused)} type="button">
              {agent.paused ? 'Resume' : 'Pause it'}
            </button>
          )}
        </span>
      </div>
    );
  }
  if (warning?.kind === 'person' && cursor?.typing) {
    return (
      <div className="presence-editor-bar is-person" data-testid="presence-editor-bar" role="status" style={{ '--presence-ink': inkFor(warning.person.slot) }}>
        <ParticipantMark initials={warning.person.initials} size={18} slot={warning.person.slot} />
        <span>{warning.message}</span>
      </div>
    );
  }
  if (status === 'unavailable') {
    return (
      <div className="presence-editor-bar" data-testid="presence-editor-bar" role="status">
        <span>Live editing is not reachable right now. Your changes are still saved; others see them when they reopen the file.</span>
      </div>
    );
  }
  return null;
}

PresenceEditorBar.propTypes = {
  actions: PropTypes.func,
  cursor: PropTypes.object,
  onOpenSession: PropTypes.func,
  onSuggest: PropTypes.func,
  path: PropTypes.string,
  projectId: PropTypes.string,
  snapshot: PropTypes.object,
  status: PropTypes.string,
  viewer: PropTypes.bool,
};
